'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Sparkles, CheckCircle, ArrowRight, Zap, Shield, Clock, Users, Award, Bot } from 'lucide-react';
import { fetchColleges, College } from '@/lib/services/collegeService';

const features = [
  { icon: <Zap size={22} />, title: 'One Smart Form', desc: 'Fill your details once. Our AI auto-fills applications for all 1000+ colleges.', color: 'bg-violet-100 text-violet-700' },
  { icon: <Bot size={22} />, title: 'AI College Matching', desc: 'Get matched to colleges based on your score, budget, location, and career goals.', color: 'bg-blue-100 text-blue-700' },
  { icon: <Shield size={22} />, title: 'Document Vault', desc: 'Upload documents once. Securely shared with all colleges you apply to.', color: 'bg-green-100 text-green-700' },
  { icon: <Clock size={22} />, title: 'Real-time Tracking', desc: 'Track all your applications in one dashboard. Never miss a deadline.', color: 'bg-amber-100 text-amber-700' },
  { icon: <Users size={22} />, title: 'Expert Counselling', desc: 'Get free guidance from certified counsellors to shortlist the best colleges.', color: 'bg-rose-100 text-rose-700' },
  { icon: <Award size={22} />, title: 'Scholarship Finder', desc: 'Automatically matched to scholarships worth ₹50,000–₹5 Lakh.', color: 'bg-teal-100 text-teal-700' },
];

const stats = [
  { num: '1,247', label: 'Partner Colleges' },
  { num: '3.2L+', label: 'Applications Sent' },
  { num: '94%', label: 'Admission Rate' },
  { num: '₹2.4Cr', label: 'Scholarships Won' },
];

const steps = [
  { num: '01', title: 'Create Profile', desc: 'Enter your academic scores, preferences, budget, and career goals in one smart form.' },
  { num: '02', title: 'AI Matching', desc: 'Our AI analyses 50+ parameters to match you with the best-fit colleges from 1000+ options.' },
  { num: '03', title: 'Review & Apply', desc: 'Review your matched colleges, shortlist your favourites, and apply with one click.' },
  { num: '04', title: 'Track & Enroll', desc: 'Track all applications in real-time. Accept your offer and complete enrollment online.' },
];

interface FormData {
  name: string;
  email: string;
  phone: string;
  stream: string;
  score: string;
  budget: string;
  location: string;
}

interface MatchedCollege extends College {
  matchScore: number;
  probability: 'High' | 'Medium' | 'Safe';
}

export default function OneApplicationPage() {
  const [formStep, setFormStep] = useState<'form' | 'matching' | 'results'>('form');
  const [formData, setFormData] = useState<FormData>({ name: '', email: '', phone: '', stream: '', score: '', budget: '', location: '' });
  const [matchProgress, setMatchProgress] = useState(0);
  const [allColleges, setAllColleges] = useState<College[]>([]);
  const [matchedColleges, setMatchedColleges] = useState<MatchedCollege[]>([]);

  useEffect(() => {
    fetchColleges().then(setAllColleges);
  }, []);

  const handleSubmit = () => {
    if (!formData.name || !formData.stream || !formData.score) return;
    setFormStep('matching');
    let progress = 0;
    const interval = setInterval(() => {
      progress += 4;
      setMatchProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        // Filter and score colleges based on stream
        const filtered = allColleges
          .filter((c) => formData.stream === '' || c.streams.includes(formData.stream as any))
          .slice(0, 8)
          .map((c, i) => ({
            ...c,
            matchScore: Math.max(72, 98 - i * 3),
            probability: (i < 2 ? 'High' : i < 5 ? 'Medium' : 'Safe') as 'High' | 'Medium' | 'Safe',
          }));
        setMatchedColleges(filtered.length > 0 ? filtered : allColleges.slice(0, 8).map((c, i) => ({
          ...c,
          matchScore: Math.max(72, 98 - i * 3),
          probability: (i < 2 ? 'High' : i < 5 ? 'Medium' : 'Safe') as 'High' | 'Medium' | 'Safe',
        })));
        setFormStep('results');
      }
    }, 80);
  };

  const updateForm = (key: keyof FormData, val: string) => setFormData(prev => ({ ...prev, [key]: val }));

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">

        {/* Hero */}
        <section className="relative bg-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary blur-3xl -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full bg-violet-400 blur-3xl translate-y-1/2" />
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm text-slate-200 mb-5 border border-white/20">
                  <Sparkles size={14} className="text-primary" />
                  <span>AI-Powered Application Platform</span>
                </div>
                <h1 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4">
                  <span className="text-primary">1 Application</span><br />
                  1000+ Colleges
                </h1>
                <p className="text-slate-300 text-lg mb-8 max-w-lg">
                  Fill one smart form. Our AI matches you to 1000+ colleges, auto-fills applications, and tracks everything — so you can focus on your future.
                </p>
                <div className="flex flex-wrap gap-6 text-sm text-slate-300">
                  {stats.map((s) => (
                    <div key={s.label}>
                      <p className="text-2xl font-extrabold text-white">{s.num}</p>
                      <p className="text-slate-400">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Application Form / Matching / Results */}
              <div className="bg-card rounded-2xl border border-border shadow-modal overflow-hidden">
                {formStep === 'form' && (
                  <div className="p-6">
                    <h2 className="text-xl font-extrabold text-foreground mb-1">Start Your Application</h2>
                    <p className="text-sm text-muted-foreground mb-5">Takes 2 minutes. Get matched to 1000+ colleges instantly.</p>
                    <div className="space-y-3">
                      <input type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => updateForm('name', e.target.value)} className="input-base" />
                      <div className="grid grid-cols-2 gap-3">
                        <input type="email" placeholder="Email *" value={formData.email} onChange={(e) => updateForm('email', e.target.value)} className="input-base" />
                        <input type="tel" placeholder="Phone" value={formData.phone} onChange={(e) => updateForm('phone', e.target.value)} className="input-base" />
                      </div>
                      <select value={formData.stream} onChange={(e) => updateForm('stream', e.target.value)} className="input-base">
                        <option value="">Select Stream *</option>
                        <option>Engineering</option>
                        <option>Medical</option>
                        <option>Management</option>
                        <option>Law</option>
                        <option>Arts</option>
                        <option>Commerce</option>
                      </select>
                      <div className="grid grid-cols-2 gap-3">
                        <input type="text" placeholder="Entrance Score *" value={formData.score} onChange={(e) => updateForm('score', e.target.value)} className="input-base" />
                        <select value={formData.budget} onChange={(e) => updateForm('budget', e.target.value)} className="input-base">
                          <option value="">Annual Budget</option>
                          <option>Under ₹1 Lakh</option>
                          <option>₹1–3 Lakh</option>
                          <option>₹3–7 Lakh</option>
                          <option>₹7 Lakh+</option>
                        </select>
                      </div>
                      <input type="text" placeholder="Preferred City / State" value={formData.location} onChange={(e) => updateForm('location', e.target.value)} className="input-base" />
                      <button onClick={handleSubmit} disabled={!formData.name || !formData.stream || !formData.score} className="btn-primary w-full py-3 text-base flex items-center justify-center gap-2">
                        <Sparkles size={16} />
                        Find My Colleges →
                      </button>
                    </div>
                  </div>
                )}

                {formStep === 'matching' && (
                  <div className="p-8 text-center">
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                      <Bot size={36} className="text-primary animate-pulse" />
                    </div>
                    <h3 className="text-xl font-extrabold text-foreground mb-2">AI Matching in Progress</h3>
                    <p className="text-muted-foreground text-sm mb-6">Analysing 1,247 colleges based on your profile...</p>
                    <div className="w-full bg-muted rounded-full h-3 mb-3">
                      <div className="bg-primary h-3 rounded-full transition-all duration-100" style={{ width: `${matchProgress}%` }} />
                    </div>
                    <p className="text-sm font-bold text-primary">{matchProgress}% complete</p>
                    <div className="mt-6 space-y-2 text-sm text-muted-foreground text-left">
                      {matchProgress > 20 && <p className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> Analysing entrance score compatibility</p>}
                      {matchProgress > 45 && <p className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> Filtering by budget & location preferences</p>}
                      {matchProgress > 70 && <p className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> Ranking by placement & career outcomes</p>}
                      {matchProgress > 90 && <p className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> Preparing your personalised shortlist</p>}
                    </div>
                  </div>
                )}

                {formStep === 'results' && (
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle size={20} className="text-green-500" />
                      <h3 className="font-extrabold text-foreground">Found {matchedColleges.length} Matched Colleges!</h3>
                    </div>
                    <div className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin">
                      {matchedColleges.map((college) => (
                        <div key={college.id} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold text-white shrink-0 ${college.probability === 'High' ? 'bg-green-500' : college.probability === 'Medium' ? 'bg-amber-500' : 'bg-blue-500'}`}>
                            {college.matchScore}%
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-xs text-foreground truncate">{college.name}</p>
                            <p className="text-xs text-muted-foreground">{college.location}</p>
                          </div>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${college.probability === 'High' ? 'bg-green-100 text-green-700' : college.probability === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                            {college.probability}
                          </span>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => { setFormStep('form'); setMatchProgress(0); }} className="btn-secondary w-full mt-3 py-2 text-sm">
                      Start Over
                    </button>
                    <button className="btn-primary w-full mt-2 py-3">
                      Apply to All Matched Colleges →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-14 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-foreground">How It Works</h2>
            <p className="text-muted-foreground text-sm mt-1">From one form to your dream college in 4 simple steps</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step, i) => (
              <div key={step.num} className="relative bg-card rounded-2xl border border-border p-6 hover:border-primary hover:shadow-md transition-all duration-200">
                <div className="text-4xl font-extrabold text-primary/20 mb-3">{step.num}</div>
                <h3 className="font-extrabold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                    <ArrowRight size={20} className="text-primary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="py-14 bg-muted/50">
          <div className="px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-foreground">Everything You Need to Apply</h2>
              <p className="text-muted-foreground text-sm mt-1">One platform. Complete application support.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.map((feat) => (
                <div key={feat.title} className="bg-card rounded-2xl border border-border p-6 hover:border-primary hover:shadow-md transition-all duration-200">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${feat.color}`}>
                    {feat.icon}
                  </div>
                  <h3 className="font-extrabold text-foreground mb-2">{feat.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
